// src/components/ui/1-molecules/ExpandableSection/ExpandableSection.js

import React, { useState, useRef, useEffect } from 'react';
import SectionHeader from '../SectionHeader/SectionHeader';
import ShowMoreButton from '../../0-atoms/ShowMoreButton/ShowMoreButton';
import styles from './ExpandableSection.module.css';

/**
 * A reusable expandable section with title and collapsible text
 * @param {string} title - Section title
 * @param {string} content - The text content to display
 * @param {number} [maxLines] - Maximum lines before truncation (default: 4)
 * @param {boolean} [defaultExpanded] - Whether to start expanded (default: false)
 * @param {string} [className] - Additional CSS classes
 * @param {string} [contentClassName] - Additional CSS classes for content
 */
const ExpandableSection = ({ 
  title,
  content,
  maxLines = 7,
  defaultExpanded = false,
  className = '',
  contentClassName = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);
  const [contentHeight, setContentHeight] = useState('auto');
  const contentRef = useRef(null);
  const innerRef = useRef(null);
  
  // Calculate line height based on screen size
  const getLineHeight = () => {
    if (typeof window !== 'undefined' && window.innerWidth <= 480) {
      // Mobile line height (matching the CSS media query)
      return 18; // Approximate line height for body4 text
    }
    return 20; // Desktop line height for body3 text
  };
  
  const [lineHeight, setLineHeight] = useState(getLineHeight());
  const maxHeight = lineHeight * maxLines;

  useEffect(() => {
    // Update line height on window resize
    const handleResize = () => {
      setLineHeight(getLineHeight());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (innerRef.current) {
      const scrollHeight = innerRef.current.scrollHeight;
      setShouldShowToggle(scrollHeight > maxHeight);
      
      // Set initial height for animation
      if (!isExpanded && scrollHeight > maxHeight) {
        setContentHeight(`${maxHeight}px`);
      } else {
        setContentHeight(`${scrollHeight}px`);
      }
    }
  }, [content, maxHeight, isExpanded]);

  // Don't render if there's no content
  if (!content || content.trim() === '') {
    return null;
  }

  const handleToggle = () => {
    if (contentRef.current && innerRef.current) {
      const scrollHeight = innerRef.current.scrollHeight;
      const computedStyle = window.getComputedStyle(innerRef.current);
      const computedLineHeight = parseFloat(computedStyle.lineHeight);
      const actualLineHeight = !isNaN(computedLineHeight) ? computedLineHeight : 20;
      const maxHeight = actualLineHeight * maxLines;
      
      if (isExpanded) {
        // Collapsing
        setContentHeight(`${scrollHeight}px`);
        // Force reflow - void operator prevents ESLint warning
        void contentRef.current.offsetHeight;
        setContentHeight(`${maxHeight}px`);
      } else {
        // Expanding
        setContentHeight(`${scrollHeight}px`);
      }
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <section className={`${styles.section} ${className}`}>
      {title && <SectionHeader title={title} />}
      
      <div className={styles.contentWrapper}>
        <div 
          ref={contentRef}
          className={`${styles.content} ${!isExpanded && shouldShowToggle ? styles.collapsed : ''}`}
          style={{
            maxHeight: shouldShowToggle ? contentHeight : 'none'
          }}
        >
          <p ref={innerRef} className={`${styles.text} ${contentClassName}`}>
            {content}
          </p>
        </div>
        
        {shouldShowToggle && (
          <ShowMoreButton 
            isExpanded={isExpanded}
            onClick={handleToggle}
          />
        )}
      </div>
    </section>
  );
};

export default ExpandableSection;